import { Box, Button, ClickAwayListener, IconButton, MenuItem, Paper, Popper, Select, Skeleton, Stack, styled, Switch, TextField, Typography } from "@mui/material";
import RegionSelector from "../region-selector";
import { useAddConquestModeMutation, useConquestModeQuery, useDisableConquestModeMutation, useRegionsConquestModeQuery, useRegionsQuery } from "src/context/api/non-branded/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { fDate } from "src/utils/format-time";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Iconify from "src/components/iconify";
import { DataGridPro, GridColumnVisibilityModel, GridPagination, GridRenderCellParams, useGridApiContext } from "@mui/x-data-grid-pro";
import { formatNumber, fPercent } from "src/utils/format-number";
import { formatCurrency } from "src/utils/format-currency";
import { useLocales } from "src/locales";
import CustomLineChart from "src/components/inline-chart/inline-chart-activation";
import ModalPreview from "./modal-preview";
import ModalCreateConquestMode from "./modal-create-conquest-mode";
import ModalDisabledConquestMode from "./modal-disabled-conquest-mode";
import { enqueueSnackbar } from "notistack";
import "./_style.scss";
import PresenceRateChart from "src/components/visibility/components/PresenceRateChart";

export default function ConquestMode() {
    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const filterStore = useSelector((state: any) => state.filters);
    const workspace: any = useCurrentWorkspace()!;
    const apiRef: any = useRef();
    const toolbarRef: any = useRef();
    const { currentLang } = useLocales();

    const [addConquestMode] = useAddConquestModeMutation();
    const [disableConquestMode] = useDisableConquestModeMutation();

    const { data: conquestModeData, isLoading: isLoadingConquestModeData, isFetching: isFetchingConquestModeData, refetch } = useConquestModeQuery({
        workspace_id: workspace?.id,
        client_id: workspace?.bqTable,
        region_name: filterStore?.countries[0]
    },
        {
            skip: !workspace?.id || !filterStore?.countries[0],
            refetchOnMountOrArgChange: true,
        }
    );

    const { data: regionsData, isLoading: isLoadingCountries, isFetching, isUninitialized } = useRegionsConquestModeQuery({
        workspace_id: workspace?.id as number,
    }, { skip: !workspace?.id });

    useEffect(() => {
        if (filterStore?.countries?.length > 0) {
            setSelectedRegion(filterStore?.countries[0]);
        }
    }, [filterStore?.countries]);

    const CustomToolbar = (props: any) => {

        return (
            <Stack
                ref={toolbarRef}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={0}
                px={2.5}
                py={1.25}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    backgroundColor: 'white',
                    borderTopRightRadius: '8px',
                    borderTopLeftRadius: '8px',
                    borderBottom: '1px dashed rgba(145, 158, 171, 0.20)',
                }}
            >

                <Box>
                    <Typography
                        sx={{
                            color: '#333C47',
                            fontFamily: '"Public Sans"',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: '120%',
                        }}
                    >
                        Competitors attacked
                    </Typography>
                </Box>

                <Stack display={"flex"} flexDirection={"row"}>
                    <IconButton
                        onClick={() => apiRef.current?.showPreferences("columns")}
                        sx={{
                            width: 26,
                            height: 26,
                            padding: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
                    </IconButton>
                    <IconButton
                        onClick={() => apiRef.current?.showFilterPanel()}
                        sx={{
                            width: 26,
                            height: 26,
                            padding: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="ic:round-filter-list" width={18} height={18} />
                    </IconButton>
                </Stack>

            </Stack>
        )
    }

    const CustomFooter = ({
        paginationModel,
        onPageSizeChange,
        rows
    }: any) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    backgroundColor: "#FAFAFA",
                    px: 2,
                    py: 1,
                    gap: 2,
                    borderBottomRightRadius: "8px",
                    borderBottomLeftRadius: "8px",

                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} height={"100%"}>
                    {rows?.length > 10 &&
                        <Stack display={"flex"} flexDirection={"row"} alignItems={"center"} gap={2}>
                            <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>Rows per page:</Typography>
                            <Select
                                size="small"
                                value={paginationModel.pageSize}
                                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                                sx={{ minWidth: 80 }}
                            >
                                {[5, 10, 20, 50].map((size) => (
                                    <MenuItem key={size} value={size}>
                                        {size}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                    }

                    <GridPagination />
                </Stack>
            </Box>
        );
    };

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

    const handleChangeColumnVisibilityModel = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
    }, []);

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });

    const [modalPreview, setModalPreview] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const [modalCreateConquestMode, setModalCreateConquestMode] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const [modalDisabledConquestMode, setModalDisabledConquestMode] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const columns = [
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row;

                const isChecked = row.isParent
                    ? row.children?.every(
                        (child: any) => child?.conquest_mode_data?.status === true
                    )
                    : row?.conquest_mode_data?.status === true;

                const handleSwitchClick = (event: React.MouseEvent) => {
                    event.stopPropagation();
                    event.preventDefault();

                    if (!isChecked) {
                        const rowsToActivate = row.isParent
                            ? row.children?.map((child: any) => {
                                if (!child.conquest_mode_data) {
                                    return { ...child, conquest_mode_data: {} };
                                }

                                if (child.conquest_mode_data.status !== true) {
                                    return child;
                                }

                                return null;
                            }).filter(Boolean) ?? []
                            : [row];

                        setModalCreateConquestMode({
                            open: true,
                            row: rowsToActivate,
                        });
                    } else {
                        const rowsToDisable = row.isParent
                            ? row.children ?? []
                            : [row];

                        setModalDisabledConquestMode({
                            open: true,
                            row: rowsToDisable,
                        });
                    }
                };

                return <Switch onClick={handleSwitchClick} checked={isChecked} />;
            },
        },
        {
            field: 'ad_clicks',
            headerName: 'Paid Clicks',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <>
                    {formatNumber(params.row.ad_clicks)}
                </>
            ),
        },
        {
            field: 'ad_cost',
            headerName: 'Ad Cost',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <>
                    {formatCurrency(params.row.ad_cost, workspace.currency, currentLang.value, true, 1)}
                </>
            ),
        },
        {
            field: 'avg_cpc',
            headerName: 'Paid CPC',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <>
                    {formatCurrency(params.row.avg_cpc, workspace.currency, currentLang.value, true, 2)}
                </>
            ),
        },
        {
            field: 'ad_conversions',
            headerName: 'Conv.',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <>
                    {formatCurrency(params.row.ad_conversions, workspace.currency, currentLang.value, true, 1)}
                </>
            ),
        },
        {
            field: 'ad_revenue',
            headerName: 'Revenue',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <>
                    {formatCurrency(params.row.ad_revenue, workspace.currency, currentLang.value, true, 1)}
                </>
            ),
        },
        {
            field: 'competitor_visibility_rate',
            headerName: 'Competitor visibility rate',
            flex: 1,
            minWidth: 260,
            renderCell: (params: GridRenderCellParams) => {

                const data = {
                    organicOnly: (params?.row?.organic_rate) || 0,
                    soloBidder: Number(params?.row?.solo_bidder) || 0,
                    topAdPosition: Number(params?.row?.top_ad) || 0,
                    competitorOnly: Number(params?.row?.competitor_only) || 0,
                };

                const total = params?.row?.visibility_rate;

                return (
                    <>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ width: '100%', height: "100%" }}
                        >
                            <Typography variant="body2">{fPercent(total?.toFixed(0))}</Typography>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                py={1}
                                sx={{ width: "100%", height: "52px" }}
                            >
                                <PresenceRateChart round={1} data={data} height={40} totalVisibilityRate={total} colors={["#919EAB", "#E95954", "#EF8783", "#2CCC87"]} />
                            </Stack>
                        </Stack>
                    </>
                )
            },
        },
        {
            field: 'paid_first',
            headerName: 'Paid First',
            cellClassName: 'sparkline-cell',
            flex: 1,
            renderCell: (params: any) => {
                const points = params.row.fitted_trend_abs_top_paid_presence_rate;

                if (!points || points.length === 0) return null;

                const series = [{
                    name: "Paid First",
                    data: points.map((point: any) => ({
                        x: Number(point.max_cpc),
                        y: point.fitted_trend_abs_top_paid_presence_rate,
                    })),
                }];

                return (
                    <Box
                        px={2}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();


                            setModalPreview({ open: true, row: params.row });
                        }}
                        height="100%"
                    >
                        <CustomLineChart
                            series={series}
                            maxCpcValue={33}
                            color="#2D0857"
                            height={45}
                        />
                    </Box>
                );
            },
        },
        {
            field: 'paid_top',
            headerName: 'Paid Top',
            cellClassName: 'sparkline-cell',

            flex: 1,
            renderCell: (params: any) => {
                const points = params.row.fitted_trend_top_paid_presence_rate;

                if (!points || points.length === 0) return null;

                const series = [{
                    name: "Paid Top",
                    data: points.map((point: any) => ({
                        x: Number(point.max_cpc),
                        y: point.fitted_trend_top_paid_presence_rate,
                    })),
                }];

                return (
                    <Box
                        px={2}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                        height="100%"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();


                            setModalPreview({ open: true, row: params.row });
                        }}
                    >
                        <CustomLineChart
                            series={series}
                            maxCpcValue={33}
                            color="#2D0857"
                            height={45}
                        />
                    </Box>
                );
            },
        },
        {
            field: 'max_cpc',
            headerName: 'Ceiling Max CPC',
            flex: 1,
            renderCell: (params: any) => {
                if (params.row.children || params?.row?.conquest_mode_data && !(Object.keys(params?.row.conquest_mode_data || {}).length > 0)) return null;

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            width: '100%',
                            cursor: 'pointer'
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();


                            setModalPreview({ open: true, row: params.row });
                        }}
                    >
                        <TextField
                            type="number"
                            value={params.row.conquest_mode_data?.max_cpc ?? ""}
                            fullWidth
                            size="small"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    cursor: 'pointer',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(145, 158, 171, 0.16)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '90%',
                                    color: 'rgba(51, 60, 71, 1)',
                                    padding: '6px 8px',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        borderColor: 'rgba(0, 108, 156, 0.40)',
                                        backgroundColor: 'rgba(0, 108, 156, 0.03)',
                                        boxShadow: '0 0 0 2px rgba(51, 138, 243, 0.20)',
                                    },
                                },
                                inputProps: {
                                    sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        padding: .5,
                                    }
                                }
                            }}
                        />
                    </Box>
                );
            },
        },
        {
            field: 'threshold',
            headerName: 'Threshold',
            flex: 1,
            renderCell: (params: any) => {
                if (params.row.children || params?.row?.conquest_mode_data && !(Object.keys(params?.row.conquest_mode_data || {}).length > 0)) return null;

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            width: '100%',
                            cursor: 'pointer'
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();


                            setModalPreview({ open: true, row: params.row });
                        }}
                    >
                        <TextField
                            type="number"
                            value={params.row.conquest_mode_data?.threshold ?? ""}
                            fullWidth
                            size="small"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    cursor: 'pointer',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(145, 158, 171, 0.16)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '90%',
                                    color: 'rgba(51, 60, 71, 1)',
                                    padding: '6px 8px',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        borderColor: 'rgba(0, 108, 156, 0.40)',
                                        backgroundColor: 'rgba(0, 108, 156, 0.03)',
                                        boxShadow: '0 0 0 2px rgba(51, 138, 243, 0.20)',
                                    },
                                },
                                inputProps: {
                                    sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        padding: .5,
                                    }
                                }
                            }}
                        />
                    </Box>
                );
            },
        },
    ];

    const isLoading = isLoadingCountries || isLoadingConquestModeData || isFetching || isFetchingConquestModeData;

    const rawRows = conquestModeData || [];

    const flattenedRows = rawRows.flatMap((adGroup: any) => {
        const parentRow = {
            ...adGroup,
            id: adGroup.id,
            treePath: [adGroup.ad_group_name],
            isParent: true
        };

        const childrenRows = (adGroup.children ?? []).map((kw: any) => ({
            ...kw,
            id: kw.id,
            treePath: [adGroup.ad_group_name, kw.keyword],
            isParent: false
        }));

        return [parentRow, ...childrenRows];
    });

    const rows = flattenedRows;
    const hasRows = rows.length > 0;

    const columnGroupingModel = [
        {
            groupId: 'ads_centric',
            headerName: 'Ads centric',
            headerClassName: "ads-centric-header",

            children: [
                { field: 'ad_clicks' },
                { field: 'ad_cost' },
                { field: 'avg_cpc' },
                { field: 'ad_conversions' },
                { field: 'ad_revenue' },
            ],
        },
        {
            groupId: 'scrapping_centric',
            headerName: 'Scrapping centric',
            headerClassName: "scrapping-centric-header",
            children: [
                { field: 'competitor_visibility_rate' },
                { field: 'paid_first' },
                { field: 'paid_top' },
            ],
        },
    ];

    const handleCloseModalPreview = useCallback(() => setModalPreview({ open: false, row: null }), []);
    const handleCloseModalCreateConquestMode = useCallback(() => setModalCreateConquestMode({ open: false, row: null }), []);
    const handleCloseModalDisabledConquestMode = useCallback(() => setModalDisabledConquestMode({ open: false, row: null }), []);

    const handleCreateConquestMode = async (dataArray: any[]) => {
        const data = dataArray.map((data) => ({
            keyword: data?.keyword,
            keyword_id: String(data?.keyword_id),
            match_type: data?.match_type,
            account: data?.account_name,
            ad_group_name: data?.ad_group_name,
            platform: data?.platform,
            campaign_name: data?.campaign_name ? [data.campaign_name] : [],
            campaign_id: data?.campaign_id ? [String(data?.campaign_id)] : [],
            account_id: String(data?.account_id),
            ad_group_id: String(data?.ad_group_id),
            workspaceId: workspace.id,
            client: workspace?.bqTable,
            mode: `Conquest Mode - ${data?.strategy || "Paid"}`,
            max_cpc: data?.max_cpc ? parseFloat(data.max_cpc) : null,
            accepted_variation_threshold: data?.accepted_variation_threshold ? parseFloat(data.accepted_variation_threshold) : null,
            campaign_type: "Search",
            bidding_strategy_type: "Max CPC",
            domain: data?.domain || null,
            country: data?.country_names,
            country_code: data?.country_codes,
            home_language: data?.home_language,
        }));

        try {
            await addConquestMode({
                workspace_id: workspace.id,
                data,
            }).unwrap();

            setModalCreateConquestMode({ open: false, row: null });

            refetch();

            enqueueSnackbar(`Conquest Mode activated successfully`, { variant: "success" });
        } catch (err) {
            console.error("Error adding Conquest Mode:", err);
            enqueueSnackbar(`Error activating Conquest Mode`, { variant: "error" });
        }
    };

    const handleDisabledConquestMode = async (dataArray: any[]) => {
        const ids: number[] = dataArray
            .map((data) => data?.conquest_mode_data?.id)
            .filter((id): id is number => !!id);

        if (ids.length === 0) return;

        try {
            const result = await disableConquestMode({
                ids,
                workspace_id: workspace.id,
            }).unwrap();

            refetch();

            enqueueSnackbar(`Conquest Mode disabled successfully`, { variant: "success" });
        } catch (err) {
            console.error("Error disabling Conquest Mode:", err);
            enqueueSnackbar(`Error disabling Conquest Mode`, { variant: "error" });
        }
    };

    const GridPanelRoot = styled(Popper, {
        name: 'MuiDataGrid',
        slot: 'Panel',
        overridesResolver: (props, styles) => styles.panel
    })(({
        theme
    }) => ({
        zIndex: (theme as any).zIndex.modal
    }));
    const GridPaperRoot = styled(Paper, {
        name: 'MuiDataGrid',
        slot: 'Paper',
        overridesResolver: (props, styles) => styles.paper
    })(({
        theme
    }) => ({
        backgroundColor: "#FFFFFF",
        minWidth: 300,
        maxHeight: 450,
        display: 'flex',
        maxWidth: `calc(100vw - ${theme.spacing(0.5)})`,
        overflow: 'auto'
    }));

    return (
        <>
            <Box sx={{ display: "flex", flexDirection: "column"}}>

                <RegionSelector data={regionsData?.data || []} loading={isLoading || isFetching || isUninitialized} />

                <Stack
                    direction="row"
                    spacing={2}
                    px={"24px"}
                    sx={{ marginTop: "10px" }}
                >
                    {isLoading
                        ? <Skeleton width={'100%'} height={700} variant="rectangular" animation="wave" sx={{
                            borderRadius: '8px',
                        }} />
                        :
                        <DataGridPro
                            classes={{
                                root: 'conquest-mode-grid',
                                columnHeaders: 'conquest-mode-grid-column-headers',
                                columnHeader: 'conquest-mode-grid-column-header',
                            }}
                            sx={{
                                height: "600px",
                                border: "1px solid rgba(145, 158, 171, 0.2)",
                                borderRadius: '8px',
                                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                                    outline: 'none !important',
                                },
                                "& .MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-withBorderColor": {
                                    backgroundColor: "#E8F4FF!important",
                                    fontWeight: 600,
                                    color: "#1B4B7A",
                                },
                                '& .MuiDataGrid-columnHeader, .MuiDataGrid-filler': {
                                    backgroundColor: 'rgba(226, 229, 233, 0.16) !important',
                                    color: "rgba(99, 115, 129, 1)",
                                    fontWeight: 'normal!important'
                                },
                                '& .MuiDataGrid-cell': {
                                    border: "1px solid rgba(145, 158, 171, 0.2)",
                                    color: 'rgba(51, 60, 71, 1)',
                                    backgroundColor: 'white',
                                },
                                "& .client-row .MuiDataGrid-cell": {
                                    backgroundColor: "rgba(0, 143, 205, 0.06)",
                                    fontWeight: "bold",
                                },
                                "& .data-not-ready .MuiDataGrid-cell": {
                                    backgroundColor: "rgba(118, 53, 220, 0.08)",
                                    fontWeight: "bold",
                                },
                                width: '100%',
                                '& .MuiDataGrid-cellContent': {
                                    overflow: 'visible !important',
                                },
                                '& .highlighted-row': {
                                    opacity: 0.3,
                                },
                                overflow: 'visible !important',
                            }}
                            groupingColDef={{
                                headerName: 'Competitors',
                                width: 250,
                                sortable: true,
                                valueGetter: (params: any) => params?.row?.ad_group_name,
                            }}
                            apiRef={apiRef}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={handleChangeColumnVisibilityModel}
                            columns={columns}
                            autoHeight={!hasRows ? false : true}
                            pagination={hasRows}
                            columnGroupingModel={columnGroupingModel}
                            columnGroupHeaderHeight={26}
                            hideFooter={!hasRows}
                            rows={rows}
                            treeData
                            getTreeDataPath={(row) => row.treePath}
                            defaultGroupingExpansionDepth={0}
                            hideFooterPagination={!hasRows}
                            hideFooterSelectedRowCount
                            density="standard"
                            disableRowSelectionOnClick
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            slots={{
                                panel(props) {
                                    const apiRefContext = useGridApiContext();
                                    const [isPlaced, setIsPlaced] = useState(false);

                                    const handleClickAway = useCallback(() => {
                                        apiRefContext.current.hidePreferences();
                                    }, [apiRefContext]);

                                    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
                                        if (event.key === 'Escape') {
                                            apiRefContext.current.hidePreferences();
                                        }
                                    }, [apiRefContext]);

                                    const modifiers = useMemo(() => [{
                                        name: 'flip',
                                        enabled: true,
                                        options: {
                                            rootBoundary: 'document'
                                        }
                                    }, {
                                        name: 'isPlaced',
                                        enabled: true,
                                        phase: 'main' as const,
                                        fn: () => {
                                            setIsPlaced(true);
                                        },
                                        effect: () => () => {
                                            setIsPlaced(false);
                                        }
                                    }] as any, []);

                                    const open = apiRef.current?.state?.preferencePanel?.open;
                                    const panelType = apiRef.current?.state?.preferencePanel?.openedPanelValue;
                                    const anchorEl = toolbarRef.current;

                                    if (!anchorEl) {
                                        return null;
                                    }

                                    const panelProps = {
                                        ...props,
                                        as: undefined,
                                        anchorEl: anchorEl,
                                        open: open,
                                    };

                                    return (
                                        <GridPanelRoot
                                            {...panelProps}
                                            placement="bottom-end"
                                            className={props.className}
                                            modifiers={modifiers}
                                        >
                                            <ClickAwayListener
                                                mouseEvent="onPointerUp"
                                                touchEvent={false}
                                                onClickAway={handleClickAway}
                                            >
                                                <GridPaperRoot
                                                    elevation={8}
                                                    onKeyDown={handleKeyDown}
                                                >
                                                    {isPlaced && props.children}
                                                </GridPaperRoot>
                                            </ClickAwayListener>
                                        </GridPanelRoot>
                                    );
                                },
                                toolbar: CustomToolbar,
                                footer: hasRows
                                    ? () => (
                                        <CustomFooter
                                            rows={rows}
                                            paginationModel={paginationModel}
                                            onPageSizeChange={(newSize: number) =>
                                                setPaginationModel((prev) => ({
                                                    ...prev,
                                                    pageSize: newSize,
                                                    page: 0,
                                                }))
                                            }
                                        />
                                    )
                                    : undefined,
                            }}
                        />
                    }
                </Stack>

                <ModalPreview
                    open={modalPreview.open}
                    row={modalPreview.row}
                    onClose={handleCloseModalPreview}
                />

                <ModalCreateConquestMode
                    open={modalCreateConquestMode.open}
                    row={modalCreateConquestMode.row}
                    onClose={handleCloseModalCreateConquestMode}
                    onSubmit={handleCreateConquestMode}
                />

                <ModalDisabledConquestMode
                    open={modalDisabledConquestMode.open}
                    row={modalDisabledConquestMode.row}
                    onClose={handleCloseModalDisabledConquestMode}
                    onSubmit={handleDisabledConquestMode}
                />
            </Box>
        </>
    )
}